import React, {useEffect, useState} from "react";
import Loading from "../Loading";
import InfoTooltip from "../tooltip/InfoTooltip";
import {useNavigate, useParams} from "react-router-dom";
import {
    Breadcrumbs,
    ButtonPrimary,
    ButtonSecondary,
    Card,
    Checkbox,
    Dropdown,
    Grid,
    Heading,
    IconPlane20,
    Input,
    Link,
    Text
} from "pac-ux-components";
import {useCookies} from "react-cookie";
import './merchant-profile.css';
import useMerchantProfile from "./useMerchantProfile";

function MerchantProfileAdd() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [psp, setPsp] = useState(null);
    const [saving, setSaving] = useState(false);
    const {tenantId, storeId} = useParams();
    const apiRoot = process.env.REACT_APP_API_ROOT_URL;
    const [validationErrors, setValidationErrors] = useState('');
    const [cookies] = useCookies(['idToken']);
    const apiHeaders = {
        'Content-Type': 'application/json', 'Authorization': 'Bearer ' + cookies.idToken
    }
    const navigate = useNavigate();

    const [merchantProfile, setMerchantProfile] = useState({
        name: "",
        ipnUrl: "",
        ipnJwtUrl: "",
        paymentProviderId: null,
        configuration: {},
        currencies: [],
        storeId: storeId,
        paymentMethods: []
    });

    const {getPSPs, getMerchantProfile, getStoreDetails, getTenantDetails, getCurrencies,
        store, setStore, tenant, setTenant, currencies,
        setCurrencies, PSPs, setPSPs, getPaymentMethodsForPsp,paymentMethods
    } =
        useMerchantProfile(tenantId, storeId, null, setMerchantProfile);



    useEffect(() => {
        // Define an async function inside useEffect
        const fetchData = async () => {
            await getStoreDetails();
            await getTenantDetails();
            await getPSPs();
            await getCurrencies();
            setLoading(false); // Only set loading to false after the data is fetched
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (Array.isArray(paymentMethods) && paymentMethods.length >= 0) {
            setMerchantProfile(prev => ({
                ...prev,
                paymentMethods: [...paymentMethods],
            }));
        }
    }, [paymentMethods]);

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setMerchantProfile((prevProfile) => ({
            ...prevProfile,
            [name]: value,
        }));
    };

    async function save() {
        setValidationErrors('');

        if (merchantProfile.paymentProviderId === null) {
            setValidationErrors('Please select a Payment Provider');
            window.scrollTo({
                top: 0, // Scroll to the top
                behavior: "smooth", // Smooth scrolling animation
            });
            setSaving(false);
            return;
        }

        if (merchantProfile.currencies.length === 0) {
            setValidationErrors('Please select at least one Currency');
            window.scrollTo({
                top: 0, // Scroll to the top
                behavior: "smooth", // Smooth scrolling animation
            });
            setSaving(false);
            return;
        }

        if (merchantProfile.paymentMethods.length === 0) {
            setValidationErrors('Please select at least one Payment Method');
            window.scrollTo({
                top: 0, // Scroll to the top
                behavior: "smooth", // Smooth scrolling animation
            });
            setSaving(false);
            return;
        }

        Object.entries(psp.configurationDataJsonSchema.properties).map(([key, value], index) => {
            if (value.type === "array") {
                merchantProfile.configuration[key] = merchantProfile.configuration[key].split(',').map(domain => domain.trim());
            }
        });

        const response = await fetch(`${apiRoot}/backoffice/payment-service-providers/${psp.id}/merchant-profiles`, {
            method: 'POST', headers: apiHeaders, body: JSON.stringify(merchantProfile),
        });
        if (!response.ok) {
            const errorMessage = await response.json();
            setValidationErrors(errorMessage.message);
            window.scrollTo({
                top: 0, // Scroll to the top
                behavior: "smooth", // Smooth scrolling animation
            });
            setSaving(false);
            return;
        } else {
            navigate("/tenants/" + tenantId + "/stores/" + storeId);
        }
    }

    const handleSelectChange = async (selectedProviderId) => {
        const selectedPsp = PSPs.find((provider) => provider.id === selectedProviderId);
        setPsp(selectedPsp);

        // Update provider and clear prior config/methods to avoid mixing states
        setMerchantProfile(prev => ({
            ...prev,
            paymentProviderId: selectedProviderId,
            configuration: {},
            paymentMethods: [],
        }));

        // Fetch methods for selected PSP, then default them as selected
        const methods = await getPaymentMethodsForPsp(selectedPsp.id);
        if (Array.isArray(methods)) {
            setMerchantProfile(prev => ({
                ...prev,
                paymentMethods: [...methods],
            }));
        }

    }

    if (loading) return (<Loading/>);
    if (error) return <p>Error: {JSON.stringify(error)}</p>;

    const paymentProviderOptions = PSPs.map((psp) => ({label: psp.name, value: psp.id}));

    return (<div className="MerchantProfileDetails" style={{width: 1047}}>
        <form onSubmit={async (e) => {
            e.preventDefault();
            setSaving(true);
            save();
        }}>
            <Breadcrumbs
                style={{height: 24}}
            >
                <Link
                    style={{color: '#2264A0'}}
                    as="a" href="/stores" underline="none">Stores</Link>
                <Link
                    style={{color: '#2264A0'}}
                    as="a" href={"/tenants/" + tenantId + "/stores/" + storeId} underline="none">Merchant
                    Profiles</Link>
                <Text>Add Merchant Profile</Text>
            </Breadcrumbs>

            <Heading color={'#10304C'} style={{paddingTop: 32, paddingBottom: 16}}>Add Merchant Profile</Heading>
            <div>Fill out all the Merchant Profile details.</div>

            <div style={{display: 'flex', alignItems: 'center', marginTop: 24, fontSize: 16}}>
                <div style={{display: 'flex', gap: '10px'}}>
                    <div style={{width: 200, height: 24}}><IconPlane20 style={{marginRight: 4}}/> {tenant.name}</div>
                    <div>
                        <div style={{width: 240, height: 24}}>
                            <span style={{fontWeight: 600}}>Store: </span>{store.name}</div>
                    </div>
                </div>
            </div>

            {validationErrors != '' && (
                <Card style={{marginTop: 24, paddingLeft: 24, paddingBottom: 24}}>
                    <div style={{display: "block", width: 400, margin: 8, marginTop: 24, color: "red"}}>
                        {validationErrors}
                    </div>
                </Card>
            )}

            <Card style={{marginTop: 24, paddingLeft: 24, paddingBottom: 48}}>


                <Grid columns="repeat(2, auto)">
                    <div style={{marginTop: 24}}>
                        <div className="h3">Merchant Profile Name</div>
                        <Input
                            required={true}
                            value={merchantProfile.name}
                            style={{width: 408}}
                            name="name"
                            onChange={handleInputChange}
                        />
                    </div>
                    <div style={{marginTop: 24}}>
                        <div className="h3">IPN Url <InfoTooltip text="URL for simple IPN (without retries)" /></div>
                        <Input
                            type="url"
                            value={merchantProfile.ipnUrl}
                            style={{width: 408}}
                            name="ipnUrl"
                            onChange={handleInputChange}
                        />
                    </div>
                    <div style={{marginTop: 24}}>
                        <div className="h3">Selected Payment Provider</div>
                        <Dropdown
                            required={true}
                            label="Payment Provider"
                            selected={merchantProfile.paymentProviderId}
                            style={{width: 408}}
                            name="paymentProviderId"
                            onChange={handleSelectChange}
                            options={paymentProviderOptions}
                        />
                    </div>
                    <div style={{marginTop: 24}}>
                        <div className="h3">JWT IPN Url <InfoTooltip text="URL for JWT formatted IPN" /></div>
                        <Input
                            type="url"
                            value={merchantProfile.ipnJwtUrl}
                            style={{width: 408}}
                            name="ipnJwtUrl"
                            onChange={handleInputChange}
                        />
                    </div>
                    <div style={{marginTop: 24}}>
                        <h3>Payment Methods</h3>
                        <Grid columns="repeat(3, auto)">
                            {paymentMethods.map((method) => (
                                <div
                                    style={{width: 150, paddingRight: 32, display: "flex", alignItems: "center"}}
                                    key={method.id}
                                >
                                    <Checkbox
                                        marked={merchantProfile.paymentMethods?.some(m => m.id === method.id) ? "selected" : "unselected"}
                                        onChange={(e) => {
                                            let updatedMethods = merchantProfile.paymentMethods ? [...merchantProfile.paymentMethods] : [];
                                            if (e.target.checked) {
                                                if (!updatedMethods.some(m => m.id === method.id)) {
                                                    updatedMethods.push(method);
                                                }
                                            } else {
                                                updatedMethods = updatedMethods.filter((m) => m.id !== method.id);
                                            }
                                            setMerchantProfile({...merchantProfile, paymentMethods: updatedMethods});
                                        }}
                                    />
                                    <span style={{marginLeft: 8}}>{method.name}</span>
                                </div>
                            ))}
                        </Grid>


                    </div>
                    <div style={{marginTop: 24}}>
                        <div className="h3">Merchant Profile ID</div>
                        <Input
                            style={{width: 408, color: "gray"}}
                            name="ipnUrl"
                            value="Will be generated on save"
                            readOnly={true}
                        />
                    </div>

                    <div style={{marginTop: 24}}>
                    </div>
                </Grid>
            </Card>

            <Grid columns="repeat(2, auto)" style={{marginTop: 48}}>
                <Card style={{paddingTop: 12, paddingLeft: 24, paddingBottom: 48, width: 488, marginRight: 32}}>
                    {psp === null ? (
                        <h3>Select a Payment Provider</h3>
                    ) : (
                        Object.entries(psp.configurationDataJsonSchema.properties).map(([key, value], index) => {
                            return (
                                <div>
                                    <h3>{key} {psp.configurationDataJsonSchema.required.includes(key) && (
                                        <span style={{color: "red"}}>*</span>
                                    )} {value.type === "array" && (<span
                                        style={{fontSize: "small", fontWeight: "normal"}}>comma seperated list</span>)}
                                    </h3>
                                    <Input
                                        type="text"
                                        type={value.format === 'uri' ? 'url' : "text"}
                                        required={psp.configurationDataJsonSchema.required.includes(key)}
                                        value={merchantProfile.configuration[key]} style={{width: 408}}
                                        onChange={(e) => {
                                            const updatedMerchantProfile = {
                                                ...merchantProfile,
                                                configuration: {
                                                    ...merchantProfile.configuration,
                                                    [key]: e.target.value,
                                                },
                                            };
                                            setMerchantProfile(updatedMerchantProfile);
                                        }}
                                    />
                                </div>
                            )
                        }))
                    }
                </Card>
                <Card style={{paddingTop: 12, paddingLeft: 24, paddingBottom: 48, width: 520}}>
                    <h3>Currencies</h3>
                    <Grid
                        columns="repeat(4, auto)"
                    >
                        {[...merchantProfile.currencies]
                            .sort((a, b) => a.localeCompare(b))
                            .map((currency, index) => (
                                <div style={{width: 100, paddingRight: 32, display: "flex", alignItems: "center"}}
                                     key={index}>
                                    <Checkbox
                                        marked={merchantProfile.currencies.includes(currency) ? "selected" : "unselected"}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                if (!merchantProfile.currencies.includes(currency)) {
                                                    merchantProfile.currencies.push(currency);
                                                }
                                            } else {
                                                merchantProfile.currencies = merchantProfile.currencies.filter(
                                                    (curr) => curr !== currency
                                                );
                                            }
                                            setMerchantProfile({...merchantProfile});
                                        }}
                                    />
                                    <span style={{marginLeft: 8}}>{currency}</span>
                                </div>
                            ))}

                        {currencies.map((currency, index) => (
                            !merchantProfile.currencies.includes(currency) && (
                                <div style={{width: 100, paddingRight: 32, display: "flex", alignItems: "center"}}
                                     key={index}>
                                    <Checkbox
                                        marked={merchantProfile.currencies.includes(currency) ? "selected" : "unselected"}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                if (!merchantProfile.currencies.includes(currency)) {
                                                    merchantProfile.currencies.push(currency);
                                                }
                                            } else {
                                                merchantProfile.currencies = merchantProfile.currencies.filter(
                                                    (curr) => curr !== currency
                                                );
                                            }
                                            setMerchantProfile({...merchantProfile});
                                        }}
                                    />
                                    <span style={{marginLeft: 8}}>{currency}</span>
                                </div>
                            )
                        ))}
                    </Grid>
                </Card>
                <div/>
                <div style={{marginTop: 16, marginBottom: 32, display: "flex", justifyContent: "flex-end"}}>
                    <a href={"/tenants/" + tenantId + "/stores/" + storeId}>
                        <ButtonSecondary
                            disabled={saving}
                        >
                            Cancel
                        </ButtonSecondary>
                    </a>
                    <ButtonPrimary
                        type="submit"
                        disabled={saving}
                        style={{marginLeft: 32}}
                    >
                        Save
                    </ButtonPrimary>
                </div>
            </Grid>
        </form>
    </div>);
}

export default MerchantProfileAdd;